import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Typography } from '@material-ui/core';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { RBCardView } from '../../components/RBCardView';
import { RBButton } from '../../components/RBButton';
import { RBTextField } from '../../components/RBTextField';
import { EditableStarRatings } from '../../components/StarRatings';
import { RBModal } from '../../components/RBModal';
import { RBAlert } from '../../components/RBAlert';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { RBEmptyBlock } from '../../components/RBEmptyBlock';

import { ShiftService } from '../../api/ShiftService';
import { PharmacyService } from '../../api/PharmacyService';
import { AuthService } from '../../api/AuthService';

import { RBContext } from '../../components/RBContext';
import { Colors, Font, FontFamily, FontWeight, Spacing } from '../../theme/Theme';
import { roleNameEnum, RoutePathConstant } from '../../util/Common';
import {
  ShiftListUiStateCarrier,
  withShiftListUiState,
} from '../../util/shiftListUiState';
import { AccountInfoContext } from '../../components/AccountInfoContext';

type ShiftSurveyRouteParams = { shiftId: string };

type ShiftSurveyLocationState = ShiftListUiStateCarrier;

type ShiftLikeForBlock = {
  id?: string;
  creator?: { id?: string } | null;
  pharmacist?: { id?: string } | null;
};

const MAX_COMMENT_LENGTH = 100;

const REVIEW_TEXTS = ['Poor', 'Below Average', 'Average', 'Good', 'Excellent'];

const getReviewText = (rating: number) => {
  if (!Number.isFinite(rating) || rating <= 0) {
    return '';
  }
  const clamped = Math.max(1, Math.min(5, Math.round(rating)));
  return REVIEW_TEXTS[clamped - 1] ?? '';
};

const ShiftSurvey: React.FC = () => {
  const history = useHistory();
  const location = useLocation<ShiftSurveyLocationState>();
  const match = useRouteMatch<ShiftSurveyRouteParams>();
  const { primaryColor } = useContext(RBContext);

  const shiftId = match.params.shiftId;
  const roleName = AuthService.getRoleName();
  const isOwner =
    roleName === roleNameEnum.owner ||
    roleName === roleNameEnum.regionalmanager;

  const [isFetchingShift, setIsFetchingShift] = useState(true);
  const [activeShift, setActiveShift] = useState<ShiftLikeForBlock | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successAlertShowing, setSuccessAlertShowing] = useState(false);

  const [blockSuccessAlertShowing, setBlockSuccessAlertShowing] = useState(false);
  const [blockFailureAlertShowing, setBlockFailureAlertShowing] = useState(false);
  const [blockAlertMessage, setBlockAlertMessage] = useState('');

  const [blockUserDialogVisible, setBlockUserDialogVisible] = useState(false);
  const [blockUserComment, setBlockUserComment] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [isBlockedByOther, setIsBlockedByOther] = useState(false);

  const isCommentRequired = useMemo(() => rating < 4, [rating]);
  const hasMinComment = useMemo(() => comment.trim().length >= 10, [comment]);
  const canSubmit = useMemo(() => {
    if (rating <= 0) {
      return false;
    }
    return !isCommentRequired || hasMinComment;
  }, [rating, isCommentRequired, hasMinComment]);

  const onChangeComment = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextValue = e.target.value ?? '';
    setComment(nextValue.slice(0, MAX_COMMENT_LENGTH));
  };

  const { accountInformation } = useContext(AccountInfoContext);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setIsFetchingShift(true);
      try {
        const shiftDetail = (await ShiftService.getShiftDetailByShiftId(
          shiftId,
        )) as any;
        if (!isMounted) {
          return;
        }
        setActiveShift(shiftDetail ?? null);

        try {
          const accountInfo = accountInformation;
          const blocks =
            accountInfo?.blockedList ??
            accountInfo?.owner?.blockedList ??
            accountInfo?.pharmacist?.blockedList ??
            [];
          const currentUserIdRaw =
            roleName === roleNameEnum.regionalmanager
              ? accountInfo?.id ?? null
              : localStorage.getItem('userId');

          const ownerIdRaw = shiftDetail?.creator?.id ?? null;
          const workerIdRaw = shiftDetail?.pharmacist?.id ?? null;
          const counterpartyIdRaw = isOwner ? workerIdRaw : ownerIdRaw;

          const currentUserId = currentUserIdRaw != null ? String(currentUserIdRaw) : null;
          const counterpartyId = counterpartyIdRaw != null ? String(counterpartyIdRaw) : null;

          const blocked =
            Array.isArray(blocks) && currentUserId && counterpartyId
              ? blocks.some(
                  (block: any) => {
                    const blockedUserId = block?.userId != null ? String(block.userId) : null;
                    const blockerId =
                      block?.blockUserId != null
                        ? String(block.blockUserId)
                        : block?.blockedByUser?.id != null
                          ? String(block.blockedByUser.id)
                          : null;
                    return blockedUserId === currentUserId && blockerId === counterpartyId;
                  },
                )
              : false;
          setIsBlockedByOther(Boolean(blocked));
        } catch (error) {
          // Silent fail; block button will remain enabled.
          setIsBlockedByOther(false);
        }
      } catch (e) {
        if (!isMounted) {
          return;
        }
        setActiveShift(null);
        setIsBlockedByOther(false);
      } finally {
        if (isMounted) {
          setIsFetchingShift(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [shiftId]);

  const onSubmit = async () => {
    if (isSubmitting || !canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const updateRating = isOwner
        ? ShiftService.updateShiftWorkerRating
        : ShiftService.updateShiftLocationRating;

      await updateRating(shiftId, {
        rating,
        comment: comment.length > 0 ? comment : null,
      });
      setSuccessAlertShowing(true);
    } catch (e) {
      const err = e as any;
      const message =
        err?.response?.data?.message ??
        err?.message ??
        'Unable to submit at this time.';
      setSubmitError(String(message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCloseBlockDialog = () => {
    if (feedbackLoading) {
      return;
    }
    setBlockUserDialogVisible(false);
    setBlockUserComment('');
  };

  const onBlockUser = async () => {
    if (!activeShift?.id || feedbackLoading) {
      return;
    }

    setFeedbackLoading(true);
    try {
      await AuthService.blockUser({
        comment: blockUserComment.length > 0 ? blockUserComment : null,
        shiftId: activeShift.id,
      });

      const payload = isOwner
        ? { id: activeShift.id, locationFeedbackGiven: true }
        : { id: activeShift.id, workerFeedbackGiven: true };
      await PharmacyService.updateShiftFeedback(payload as any);

      setBlockUserDialogVisible(false);
      setBlockUserComment('');
      setIsBlockedByOther(true);

      setBlockAlertMessage(
        isOwner
          ? 'The worker has been blocked successfully.'
          : 'The owner has been blocked successfully.',
      );
      setBlockSuccessAlertShowing(true);
    } catch (e) {
      console.log('onBlockUser', e);

      setBlockUserDialogVisible(false);
      setBlockAlertMessage('Unable to block at this time. Please try again later.');
      setBlockFailureAlertShowing(true);
    } finally {
      setFeedbackLoading(false);
    }
  };

  if (isFetchingShift) {
    return <LoadingIndicator />;
  }

  const blockQuestion = isOwner
    ? 'Are you sure you want to block this worker?'
    : 'Are you sure you want to block this owner?';
  const blockWarning = isOwner
    ? '*If you select "Block", this worker will not be matched with your future shifts.'
    : '*If you select "Block", you will not be matched with this owner again.';

  const goBackAfterSubmit = () => {
    const shiftListUiState = location.state?.shiftListUiState;

    if (typeof shiftListUiState !== 'undefined') {
      history.push(RoutePathConstant.Shifts, withShiftListUiState(shiftListUiState));
      return;
    }

    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.replace(RoutePathConstant.Payroll);
  };

  return (
    <div style={{ minHeight: '100%' }}>
      <RBAlert
        show={successAlertShowing}
        alertTitle="Rating Submitted"
        alertMessage="Your rating has been submitted successfully."
        buttons={[
          <RBButton
            key="ok"
            text="Thank You!"
            onClick={() => {
              setSuccessAlertShowing(false);
              goBackAfterSubmit();
            }}
            buttonBackgroundColor={primaryColor ?? Colors.themeDarkBlue}
          />,
        ]}
      />

      <RBAlert
        show={blockSuccessAlertShowing}
        alertTitle="Blocked Successfully"
        alertMessage={blockAlertMessage}
        buttons={[
          <RBButton
            key="ok"
            text="OK"
            onClick={() => setBlockSuccessAlertShowing(false)}
            buttonBackgroundColor={primaryColor ?? Colors.themeDarkBlue}
          />,
        ]}
      />

      <RBAlert
        show={blockFailureAlertShowing}
        alertTitle="Block Failed"
        alertMessage={blockAlertMessage}
        buttons={[
          <RBButton
            key="ok"
            text="OK"
            onClick={() => setBlockFailureAlertShowing(false)}
            buttonBackgroundColor={primaryColor ?? Colors.themeDarkBlue}
          />,
        ]}
      />

      <RBModal
        open={blockUserDialogVisible}
        onClose={onCloseBlockDialog}
        modalTitle={"Block"}
        backDropEnabled={!feedbackLoading}
        modalWidth={500}
        header={
          <RBEmptyBlock
            style={{
              width: '100%',
              height: Spacing.large,
              backgroundColor: Colors.defaultGrey,
              flexShrink: 0,
            }}
          />
        }
      >
        <div
          style={{
            backgroundColor: Colors.defaultGrey,
            display: 'flex',
            flex: 1,
          }}
        >
          <RBCardView
            style={{
              flex: 1,
              margin: Spacing.large,
              padding: Spacing.large,
            }}
          >
            <Typography
              style={{
                // fontFamily: FontFamily.family,
                fontSize: Font.default,
                fontWeight: FontWeight.bold as React.CSSProperties['fontWeight'],
              }}
            >
              {blockQuestion}
            </Typography>

            <div style={{ marginTop: Spacing.large }}>
              <RBTextField
                placeholder="*Please leave a comment if you decide no."
                value={blockUserComment}
                onChange={(e) =>
                  setBlockUserComment((e.target as HTMLInputElement).value)
                }
                multiline
                rows={4}
              />
            </div>

            <div style={{ marginTop: Spacing.large, color: Colors.red }}>
              <Typography
                style={{
                //   fontFamily: FontFamily.family,
                  fontSize: Font.small,
                }}
              >
                {blockWarning}
              </Typography>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: Spacing.large,
              }}
            >
              <RBButton
                buttonBackgroundColor={primaryColor}
                isLoading={feedbackLoading}
                disabled={blockUserComment.length === 0 || feedbackLoading}
                text="Block"
                onClick={onBlockUser}
                style={{ flex: 1, padding: Spacing.medium, marginRight: Spacing.small }}
              />
              <RBButton
                buttonBackgroundColor={primaryColor}
                disabled={feedbackLoading}
                text="Cancel"
                onClick={onCloseBlockDialog}
                style={{ flex: 1, padding: Spacing.medium, marginLeft: Spacing.small }}
              />
            </div>
          </RBCardView>
        </div>
      </RBModal>

      <RBCardView style={{ margin: Spacing.large, padding: Spacing.large, alignItems: 'center' }}>
        {submitError && (
          <div style={{ color: Colors.red, marginBottom: Spacing.large }}>
            {submitError}
          </div>
        )}

        <Typography
          style={{
            fontSize: Font.larger,
            // fontWeight: FontWeight.bold as React.CSSProperties['fontWeight'],
            marginBottom: Spacing.large,
          }}
        >
          Please Rate Shift
        </Typography>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography style={{ color: '#ffb400', marginBottom: Spacing.small }}>
            {getReviewText(rating)}
          </Typography>
          <EditableStarRatings
            defaultRating={rating}
            onValueChange={(value: number) => setRating(value)}
          />
        </div>

        <div style={{ width: '100%', marginTop: Spacing.large }}>
          <RBTextField
            placeholder="Please share why you rated the shift this way."
            value={comment}
            onChange={onChangeComment}
            multiline
            rows={4}
            inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: Spacing.smallest }}>
            <Typography style={{ color: Colors.darkGrey, fontSize: Font.small }}>
              {`${comment.length}/${MAX_COMMENT_LENGTH}`}
            </Typography>
          </div>

          {isCommentRequired && (
            <div style={{ marginTop: Spacing.small }}>
              <Typography
                style={{
                  color: hasMinComment ? Colors.darkGrey : Colors.red,
                  fontSize: Font.small,
                }}
              >
                Required: Please share why you rated the shift this way (min 10 characters).
              </Typography>
            </div>
          )}
        </div>
      </RBCardView>

      <div style={{ display: 'flex', flexDirection: 'row', margin: Spacing.large, marginTop: 0 }}>
        <RBButton
          buttonBackgroundColor={primaryColor}
          text={
            isBlockedByOther
              ? 'Already Blocked'
              : isOwner
                ? 'Block User'
                : 'Block Owner'
          }
          disabled={isBlockedByOther}
          onClick={() => setBlockUserDialogVisible(true)}
          style={{ flex: 1, padding: Spacing.large, marginRight: Spacing.small }}
        />
        <RBButton
          buttonBackgroundColor={primaryColor}
          isLoading={isSubmitting}
          disabled={!canSubmit || isSubmitting}
          text="Submit"
          onClick={onSubmit}
          style={{ flex: 1, padding: Spacing.large, marginLeft: Spacing.small }}
        />
      </div>
    </div>
  );
};

export default ShiftSurvey;
